import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LogbookTemplateEditData } from '../types/loogBookTypes';
import { useCreateOrUpdateLogbookTemplate } from './useCreateOrUpdateLogbookTemplate';
import { useLogbookFormState } from './useLogbookFormState';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { LOGBOOK_SAVED } from '../../../modules/analytics/events';

interface UseLogbookFormProps {
  initialData?: LogbookTemplateEditData;
  onClose: () => void;
}

export const useLogbookForm = ({ initialData, onClose }: UseLogbookFormProps) => {
  const { t } = useTranslation();
  const {
    formState,
    handleInputChange,
    handleFileSelect,
    handleFileRemove,
    handleRotate,
  } = useLogbookFormState(initialData);
  const queryClient = useQueryClient();
  const mutation = useCreateOrUpdateLogbookTemplate({
    onSuccess: () => {
      toast.success(initialData
        ? t('logbook.templates.updateSuccess', 'Logbook template updated successfully')
        : t('logbook.templates.createSuccess', 'Logbook template created successfully'));
      queryClient.invalidateQueries({ queryKey: ['logbookTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['logbookTemplate', initialData?.logbookTemplateId] });
      trackEvent(LOGBOOK_SAVED, { mode: initialData ? 'update' : 'create', logbookTemplateId: initialData?.logbookTemplateId || '', logbookName: formState.name || '' });
      onClose();
    },
  });

  const { isPending, error: mutationError } = mutation;

  const isFormValid = useMemo(() => {
    const { name, description, type, category, previewUrl } = formState;
    return (
      name.trim() !== '' &&
      description.trim() !== '' &&
      type.trim() !== '' &&
      category.trim() !== '' &&
      previewUrl !== null
    );
  }, [formState]);

  const handleSubmit = useCallback(() => {
    if (!isFormValid) return;
    const formData = new FormData();
    formData.append('name', formState.name);
    formData.append('description', formState.description);
    formData.append('type', formState.type);
    formData.append('category', formState.category);
    if (formState.logbookTemplateId) {
      formData.append('logbookTemplateId', formState.logbookTemplateId);
    }

    if (formState.file) {
      formData.append('file', formState.file);
    } else if (formState.attachmentId) {
      formData.append('attachmentId', formState.attachmentId);
    }

    mutation.mutate(formData);
  }, [isFormValid, formState, mutation]);

  const actions = useMemo(() => ([
    {
      text: t('common.cancel', 'Cancel'),
      variant: 'outlined',
      color: 'primary',
      onClick: onClose,
      disabled: isPending,
    },
    {
      text: isPending
        ? t('logbook.templates.saving', 'Saving...')
        : (initialData ? t('common.update', 'Update') : t('common.submit', 'Submit')),
      variant: 'contained',
      color: 'primary',
      onClick: handleSubmit,
      disabled: !isFormValid || isPending,
    },
  ]), [t, isPending, isFormValid, initialData, onClose, handleSubmit]);

  return {
    formState,
    actions,
    isPending,
    mutationError,
    handleInputChange,
    handleFileSelect,
    handleFileRemove,
    handleRotate,
  };
};